/**
 * AI分析用プロンプト生成ユーティリティ
 */
import i18n from './i18n';
import { getPurposeTypeNameByCode } from '../contexts/constants';

// =============================================================================
// タイトル定数
// =============================================================================

/**
 * AI分析モーダルのタイトル（getterで動的に翻訳取得）
 * 言語切替時は次回参照時に自動的に切り替わる
 */
export const AI_ANALYSIS_TITLES = {
  /** 新製品シミュレーション用 */
  get SIMULATION_NEW_PRODUCT(): string {
    return i18n.t('common.constants.aiAnalysisTitles.simulationNewProduct', { defaultValue: 'AI 生産能力分析' });
  },
  /** 更新前後サマリー用 */
  get UPDATE_SUMMARY(): string {
    return i18n.t('common.constants.aiAnalysisTitles.updateSummary', { defaultValue: 'AI 設備寿命・更新分析' });
  },
  /** 製造ナレッジ検索用 */
  get MANUFACTURING_KNOWLEDGE(): string {
    return i18n.t('common.constants.aiAnalysisTitles.manufacturingKnowledge', { defaultValue: 'AI 製造ナレッジ検索' });
  },
};

// =============================================================================
// 型定義
// =============================================================================

/**
 * 生産キャパ分析用データ
 */
export interface ProductionCapacityData {
  /** 月ごとの受注数量データ */
  adjustedOrders: number[];
  /** 月ごとの生産キャパデータ */
  productionCapacityData: number[];
}

/**
 * 更新前後サマリー用 工程・設備データ
 */
export interface UpdateSummaryProcessData {
  /** 工程名 */
  processName: string;
  /** 設備名リスト */
  equipmentNames: string[];
  /** 設備分類リスト */
  equipmentGroupNames?: string[];
  /** 償却年数リスト */
  depreciationPeriods?: number[];
}

/**
 * 製造ナレッジ検索用データ
 */
export interface ManufacturingKnowledgeData {
  /** MMI製品名 */
  productName?: string;
  /** 顧客製品名 */
  customerProductName?: string;
  /** 顧客名 */
  customerName?: string;
  /** 顧客業界 */
  customerIndustryHierarchy?: string;
  /** 用途 */
  applicationCategory?: string;
  /** 車種モデル */
  carModel?: string;
  /** パーツNo */
  partNo?: string;
  /** 投資目的 */
  purposeOfInvestment?: string;
  /** 工程名 */
  processName?: string;
  /** 設備名リスト */
  equipmentNames?: string[];
  /** 設備分類リスト */
  equipmentGroupNames?: string[];
}

// =============================================================================
// 共通テキスト
// =============================================================================

/**
 * 製造10の心得
 */
export const MANUFACTURING_PRINCIPLES = `**製造 10の心得**
1. 歩留り悪く、タクトが早まらない工程は、自ら工程改善する。
2. 不良が発生するのは加工精度を知らないからだ。判別・検査をやめるよう加工精度を極めよ。
3. ポカミスをなくす（ポカヨケ、ダブルチェック、インターロック、フールプルーフ、フェールセーフ）。
4. ボトルネック工程の生産性を極限まで追求しよう。
5. 「1当たりの生産性を過去最高に」いつもチャレンジしよう。
6. 過去実績で平準化した生産計画を立てる。
7. 人員の配置はマネジャーや事業部長が自ら考え、調整する。
8. スクラップを減らす。他リスクの改善項目を常に持て。
9. 高利益部門では営業部員・製造部員などの使い分けを徹底し、不利益行為を監視する。
10. 購入品の受け入れ検査をなくす。`;

// =============================================================================
// プロンプト生成関数
// =============================================================================

/**
 * 新製品シミュレーション用プロンプト生成
 * @param data 生産キャパデータ
 * @returns プロンプト文字列
 */
export const generateSimulationNewProductPrompt = (
  data: ProductionCapacityData
): string => {
  const { adjustedOrders, productionCapacityData } = data;

  // 最大受注を計算
  const maxOrders = adjustedOrders.length > 0 ? Math.max(...adjustedOrders) : 0;

  // 平均生産キャパを計算
  const avgCapacity =
    productionCapacityData.length > 0
      ? productionCapacityData.reduce((sum, cap) => sum + cap, 0) / productionCapacityData.length
      : 0;

  // キャパシティ判定
  const capacityRelation = avgCapacity > maxOrders ? "上回る" : "下回る";

  // キャパ余剰期間の計算
  let excessCapacityMonths = 0;
  let totalMonths = 0;
  adjustedOrders.forEach((orders, index) => {
    if (orders > 0 && productionCapacityData[index] > 0) {
      totalMonths++;
      if (productionCapacityData[index] > orders) {
        excessCapacityMonths++;
      }
    }
  });
  const excessPercentage = totalMonths > 0 ? (excessCapacityMonths / totalMonths) * 100 : 0;

  // プロンプトを作成
  return `現在、以下のように、受注の月間数量${Math.round(maxOrders).toLocaleString()}個を${capacityRelation}${Math.round(avgCapacity).toLocaleString()}個の生産キャパがあります。
受注最高値: ${Math.round(maxOrders).toLocaleString()}個
生産キャパ平均: ${Math.round(avgCapacity).toLocaleString()}個
キャパ余剰期間: ${excessPercentage.toFixed(1)}%(${excessCapacityMonths}/${totalMonths}か月)

${totalMonths}か月の内、${excessCapacityMonths}か月で${capacityRelation === "上回る" ? "上回" : "下回"}っています。

狙いはキャッシュフローをよくすることです。

また、回答の中に下記の「製造 10の心得」を上手く組み込んでください：

${MANUFACTURING_PRINCIPLES}

これらの心得を踏まえた上で、キャパシティ最適化と利益改善の具体策を提案してください。`;
};

/**
 * 更新前後サマリー用プロンプト生成
 * @param processData 工程・設備データ
 * @returns プロンプト文字列
 */
export const generateUpdateSummaryPrompt = (
  processData: UpdateSummaryProcessData
): string => {
  const equipmentText = processData.equipmentNames.length > 0
    ? processData.equipmentNames.join(", ")
    : "未設定";
  const groupText = processData.equipmentGroupNames?.filter(Boolean).length
    ? [...new Set(processData.equipmentGroupNames)].join(", ")
    : "未設定";
  const depreciationText = processData.depreciationPeriods?.filter(v => v > 0).length
    ? [...new Set(processData.depreciationPeriods.filter(v => v > 0))].map(v => `${v}年`).join(", ")
    : "未設定";

  // プロンプトを作成
  return `この設備投資案件では、以下の工程で設備の追加・更新を計画しています。

## 工程・設備情報
- 工程名: ${processData.processName}
- 設備名: ${equipmentText}
- 設備分類: ${groupText}
- 償却年数: ${depreciationText}

## 分析依頼
上記の設備について、以下の観点から詳細な分析・アドバイスをお願いします：

1. **法定耐用年数と実寿命の目安**
   - 税務上の減価償却期間
   - 適切なメンテナンスを行った場合の実寿命
   - 制御装置の寿命

2. **部位別の寿命目安と注意点**
   - 機械本体
   - 油圧・空圧機器
   - 電気・制御系
   - 周辺自動機

3. **寿命を延ばすためのメンテナンスポイント**
   - 定期点検項目
   - 作動油・消耗品の管理
   - レトロフィット（更新）の可能性

4. **更新タイミングの判断基準**
   - 物理的な寿命の兆候
   - 精度・品質面での限界
   - 経営・税務面からの判断ポイント

5. **推奨されるアクション**
   - チョコ停の頻度分析
   - 精度の傾向管理（Cp値）
   - 制御機器の予備品確認

具体的な数値（年数、時間、費用目安など）を交えて、設備投資申請の根拠として活用できる形で回答してください。`;
};

/**
 * 製造ナレッジ検索用プロンプト生成
 * @param data プロジェクト・工程・設備データ
 * @returns プロンプト文字列
 */
export const generateManufacturingKnowledgePrompt = (
  data: ManufacturingKnowledgeData
): string => {
  const productText = data.productName || data.customerProductName || "未設定";
  const customerText = data.customerName || "未設定";
  const industryText = data.customerIndustryHierarchy || "未設定";
  const applicationText = data.applicationCategory || "未設定";
  const carModelText = data.carModel || "未設定";
  const partNoText = data.partNo || "未設定";
  const purposeText = data.purposeOfInvestment
    ? (getPurposeTypeNameByCode(data.purposeOfInvestment) || data.purposeOfInvestment)
    : "未設定";
  const processText = data.processName || "未設定";
  const equipmentText = data.equipmentNames?.filter(Boolean).length
    ? [...new Set(data.equipmentNames)].join(", ")
    : "未設定";
  const groupText = data.equipmentGroupNames?.filter(Boolean).length
    ? [...new Set(data.equipmentGroupNames)].join(", ")
    : "未設定";

  return `以下の設備投資案件について、製造ナレッジの観点からアドバイスをお願いします。

## 案件情報
- 製品名: ${productText}
- 顧客名: ${customerText}
- 顧客業界: ${industryText}
- 用途: ${applicationText}
- 車種モデル: ${carModelText}
- パーツNo: ${partNoText}
- 投資目的: ${purposeText}

## 工程・設備情報
- 工程名: ${processText}
- 設備名: ${equipmentText}
- 設備分類: ${groupText}

## アドバイス依頼
上記の情報を踏まえて、以下の観点から最新の製造業ナレッジを提供してください：

1. **市場動向と競合分析**
   - この製品を製造している主要企業
   - 市場規模と成長率
   - 参入障壁と成功要因

2. **製造手法の最新トレンド**
   - この製品に適した最新の製造技術・手法
   - 従来手法との比較（品質、コスト、生産性）
   - 自動化・DXの適用可能性

3. **推奨される設備構成**
   - この製品の製造に最適な設備ラインナップ
   - 設備メーカーの選定ポイント
   - 投資対効果の高い設備投資戦略

4. **品質・規格要件**
   - 業界固有の品質規格（自動車向けの場合: IATF16949等）
   - 必要な検査設備・工程内品質管理
   - トレーサビリティ要件

5. **設備投資申請への活用ポイント**
   - この投資の妥当性を裏付ける市場データ
   - ROI最大化のための提案
   - リスク要因と対策

具体的な数値やデータを交えて、設備投資申請の根拠として活用できる形で回答してください。`;
};
